import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex flex-wrap gutter-y-s3 flex-grow-1" }
const _hoisted_2 = { class: "w-12 sm:w-4" }
const _hoisted_3 = { class: "flex flex-column" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "w-12 sm:w-4" }
const _hoisted_6 = { class: "flex flex-column" }
const _hoisted_7 = { class: "w-12 sm:w-4" }
const _hoisted_8 = { class: "flex flex-column" }
const _hoisted_9 = {
  key: 1,
  class: "opacity-30"
}
const _hoisted_10 = {
  key: 1,
  class: "opacity-30"
}

import Caption from '@/components/Caption.vue';
import { Portfolio } from '@/sdk/entities/portfolio';
import { format } from '@/utils/format';
import LpTokenPriceChart from '@/views/pages/liquidity/portfolios/portfolio/portfolioHeader/LpTokenPriceChart.vue';
import { PortfolioSource } from '@/sdk/entities/PortfolioSource';
import { IntervalType, toQueryNetwork, useGetStatPortfoliosQuery } from '@/helpers/apollo/query';
import { computed, ref, watch } from 'vue';
import { SELECTED_NETWORK_NAME } from '@/helpers/networkParams.helper';
import { NETWORK_SUPPORTS_FARMS } from '@/helpers/supporting-features-in-network';
import { useStore } from 'vuex';
import { isAnalyticNetworkSupported } from '@/helpers/analytic.helper';
import { getPortfolioApr } from '@/helpers/portfolioApr.helper';
import { useI18n } from 'vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'PortfolioHeaderData',
  props: {
    portfolio: null
  },
  setup(__props: any) {

const props = __props as {
  portfolio: Portfolio;
}



const store = useStore();
const { t } = useI18n();
const pricesData = ref();
const isAnalyticsLoading = ref(true);
const isPricesDataError = ref(false);
const isAnalyticsSupported = computed(
  () =>
    isAnalyticNetworkSupported.value && props.portfolio.type === PortfolioSource.PORTFOLIO_LOCALE,
);
const isFarmSupported = computed(() => NETWORK_SUPPORTS_FARMS);

const isAprLoading = computed(() => {
  return !store.state.farming.isStoreReady;
});

const portfolioApr = computed(() =>
  !isAprLoading.value && isFarmSupported.value
    ? getPortfolioApr(props.portfolio)
    : props.portfolio.printIPR,
);
const apr = computed(() => {
  if (portfolioApr.value === undefined) {
    return '';
  }
  const apr = portfolioApr.value;
  if (apr < 1) {
    return `< 1%`;
  }
  return apr > 10000 ? `> 10 000%` : `${format(apr.toFixed(2))}%`;
});

const liquidityInUSDFormatted = computed(() => {
  loggingLiquidityInUSD();
  return format(props.portfolio.getValueInUSD(props.portfolio.portfolioTotalValueBase.toExact()));
});

if (props.portfolio.type === PortfolioSource.PORTFOLIO_LOCALE) {
  const today = new Date();
  const priorDate = new Date(new Date().setDate(today.getDate() - 30));

  const statPortfoliosArgs = {
    start: priorDate.toISOString().split('Z')[0],
    end: today.toISOString().split('Z')[0],
    interval: IntervalType.Hours_24,
    tokenIntervalDatasOrderBy: 'dateTime',
    tokenAddress: props.portfolio.lpTokenAddress,
    //TODO get from charts link
    network: toQueryNetwork(SELECTED_NETWORK_NAME),
  };
  loggingGetStatPortfoliosArgs(statPortfoliosArgs);
  const { result, loading, error } = useGetStatPortfoliosQuery({ ...statPortfoliosArgs });

  watch(result, () => {
    pricesData.value = result.value?.tokenIntervalDatas?.map(item => item?.priceUSD);
  });
  watch(loading, () => (isAnalyticsLoading.value = loading.value));
  watch(error, () => {
    console.error('[PORTFOLIO:APOLLO:STAT] Error : ', error.value);
    isPricesDataError.value = true;
  });
}

// DEBUG

function isLoggingPortfoliosDisabled() {
  return !window['BLUESHIFT_DEBUG'].PORTFOLIOS;
}

function loggingGetStatPortfoliosArgs(args: any) {
  if (isLoggingPortfoliosDisabled()) return;

  console.log('[PORTFOLIO:APOLLO:GET_STAT_PORTFOLIOS] params : ', args);
}

function loggingLiquidityInUSD() {
  if (isLoggingPortfoliosDisabled()) return;

  console.groupCollapsed(
    `[PORTFOLIO] [${props.portfolio.name} | ${props.portfolio.contractAddress}] Liquidity In USD`,
  );
  console.log('priceInUSD : ', props.portfolio.priceInUSD.toString());
  console.log('portfolioTotalValueBase : ', props.portfolio.portfolioTotalValueBase.toExact());
  console.groupEnd();
}

return (_ctx: any,_cache: any) => {
  const _component_Skeleton = _resolveComponent("Skeleton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_unref(apr))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(Caption, {
              caption: _unref(t)('portfolio.apr.caption'),
              tooltip: _unref(t)('portfolio.apr.tooltip'),
              class: "mb-s1"
            }, null, 8, ["caption", "tooltip"]),
            _createElementVNode("div", _hoisted_3, [
              (_unref(isAprLoading))
                ? (_openBlock(), _createBlock(_component_Skeleton, {
                    key: 0,
                    height: "24px",
                    width: "50px"
                  }))
                : (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(apr)), 1))
            ])
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(Caption, {
        caption: _ctx.$t('portfolio.liquidity.caption'),
        class: "mb-s1"
      }, null, 8, ["caption"]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", null, "$" + _toDisplayString(_unref(liquidityInUSDFormatted)), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(Caption, {
        caption: _unref(t)('portfolio.lpTokenPrice30.caption'),
        class: "mb-s1"
      }, null, 8, ["caption"]),
      _createElementVNode("div", _hoisted_8, [
        (_unref(isAnalyticsSupported))
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (isAnalyticsLoading.value)
                ? (_openBlock(), _createBlock(_component_Skeleton, {
                    key: 0,
                    height: "28px"
                  }))
                : (isPricesDataError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, "-- No data --"))
                  : (_openBlock(), _createBlock(LpTokenPriceChart, {
                      key: 2,
                      prices: pricesData.value
                    }, null, 8, ["prices"]))
            ], 64))
          : (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_unref(t)('comingSoon')), 1))
      ])
    ])
  ]))
}
}

})